import React, { useEffect, useState } from 'react';
import { CloudOff, Cloud, Check } from 'lucide-react';

/**
 * Compact status badge that lives next to the intake progress bar.
 *
 * Three states:
 *   - "online + saved"   : "Saved · 2:13 PM"  (subtle, copper)
 *   - "offline"          : "Offline — saved on this device" (warm warning)
 *   - "pending submit"   : "Will submit when you're back online" (warm warning)
 *
 * Keeps the patient calm: their work is never lost, even with no signal.
 */
const OfflineIntakeBadge = ({ savedAt, hasPendingSubmit }) => {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const fmtTime = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // Priority: pending submit > offline > online-saved
  if (hasPendingSubmit) {
    return (
      <div className="intake-offline-badge intake-offline-badge--pending" data-testid="intake-offline-badge">
        <CloudOff size={14} aria-hidden="true" />
        <span>Submission queued &mdash; will send when you&rsquo;re back online</span>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="intake-offline-badge intake-offline-badge--offline" data-testid="intake-offline-badge">
        <CloudOff size={14} aria-hidden="true" />
        <span>Offline &mdash; your work is saved on this device</span>
      </div>
    );
  }

  if (savedAt) {
    return (
      <div className="intake-offline-badge intake-offline-badge--online" data-testid="intake-offline-badge">
        <Check size={14} aria-hidden="true" />
        <span>Saved &middot; {fmtTime(savedAt)}</span>
      </div>
    );
  }

  // Online but nothing saved yet — show neutral cloud
  return (
    <div className="intake-offline-badge intake-offline-badge--idle" data-testid="intake-offline-badge">
      <Cloud size={14} aria-hidden="true" />
      <span>Auto-saving</span>
    </div>
  );
};

export default OfflineIntakeBadge;
